class ProductDescriptionTabs extends HTMLElement {
  connectedCallback() {
    this.classList.add('is-reveal-ready');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.buildTabsFromDescription();
    this.tabs = Array.from(this.querySelectorAll('[data-ppdt-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-ppdt-panel]'));

    this.addEventListener('click', this.onClick);
    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.setupReveal();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('keydown', this.onKeyDown);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.observer?.disconnect();
  }

  onClick = (event) => {
    const tab = event.target.closest('[data-ppdt-tab]');
    if (!tab || !this.contains(tab)) return;

    // These tabs can be rendered near product forms and inside Horizon's custom
    // desktop scroll container. Never allow a tab click to become a form action
    // or bubble into a theme-level click handler.
    event.preventDefault();
    event.stopPropagation();
    this.selectTab(this.tabs.indexOf(tab), false);
  };

  onPointerDown = (event) => {
    const tab = event.target.closest('[data-ppdt-tab]');
    if (!tab || !this.contains(tab)) return;
    const rect = tab.getBoundingClientRect();
    const ripple = document.createElement('span');
    ripple.className = 'ppdt__ripple';
    ripple.style.left = `${event.clientX - rect.left}px`;
    ripple.style.top = `${event.clientY - rect.top}px`;
    tab.append(ripple);
    ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
  };

  onKeyDown = (event) => {
    const current = event.target.closest('[data-ppdt-tab]');
    if (!current || !this.contains(current) || this.tabs.length === 0) return;
    const index = this.tabs.indexOf(current);
    let next = index;

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.tabs.length - 1;
    else return;

    event.preventDefault();
    this.selectTab(next, true);
  };

  onBlockSelect = (event) => {
    const blockId = event.detail?.blockId || event.target?.dataset?.blockId;
    const index = this.tabs.findIndex((tab) => tab.dataset.blockId === blockId);
    if (index >= 0) this.selectTab(index, false);
  };

  selectTab(index, focusTab) {
    const tab = this.tabs[index];
    const panel = this.panels[index];
    if (!tab || !panel) return;

    const scrollPositions = this.captureVerticalScrollPositions();

    this.tabs.forEach((item, itemIndex) => {
      const active = itemIndex === index;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', active ? 'true' : 'false');
      item.tabIndex = active ? 0 : -1;
    });

    this.panels.forEach((item, itemIndex) => {
      const active = itemIndex === index;
      item.hidden = !active;
      item.classList.toggle('is-active', active);
      item.classList.remove('is-entering');
    });

    if (this.dataset.animation !== 'none' && !this.reducedMotion.matches) {
      requestAnimationFrame(() => panel.classList.add('is-entering'));
    }

    this.centerTabHorizontally(tab);
    if (focusTab) tab.focus({ preventScroll: true });
    this.restoreVerticalScrollPositions(scrollPositions);
  }

  captureVerticalScrollPositions() {
    const candidates = [
      document.querySelector('.page-wrapper'),
      document.scrollingElement,
      document.documentElement,
      document.body,
    ];
    const seen = new Set();

    return candidates
      .filter((element) => {
        if (!element || seen.has(element)) return false;
        seen.add(element);
        return true;
      })
      .map((element) => ({ element, top: element.scrollTop }));
  }

  restoreVerticalScrollPositions(positions) {
    const restore = () => {
      positions.forEach(({ element, top }) => {
        if (element.scrollTop !== top) element.scrollTop = top;
      });
    };

    // Restore immediately for the current layout and again after the browser has
    // processed the hidden-panel swap and animation frame.
    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });
  }

  centerTabHorizontally(tab) {
    const track = tab.closest('.ppdt__tabs');
    if (!track || track.scrollWidth <= track.clientWidth + 1) return;
    const targetLeft = tab.offsetLeft - (track.clientWidth - tab.offsetWidth) / 2;
    track.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  buildTabsFromDescription() {
    if (this.dataset.autoDescriptionTabs !== 'true') return;
    const template = this.querySelector('[data-ppdt-description-template]');
    const tabsContainer = this.querySelector('.ppdt__tabs');
    const panelsContainer = this.querySelector('.ppdt__panels');
    if (!(template instanceof HTMLTemplateElement) || !tabsContainer || !panelsContainer) return;

    const source = document.createElement('div');
    source.append(template.content.cloneNode(true));
    const headings = Array.from(source.querySelectorAll('h3')).filter((heading) => heading.textContent?.trim());
    if (headings.length === 0) return;

    const tabsFragment = document.createDocumentFragment();
    const panelsFragment = document.createDocumentFragment();

    headings.forEach((heading, index) => {
      const title = heading.textContent.trim();
      const suffix = `${this.dataset.sectionId || 'section'}-${index + 1}`;
      const tabId = `PpdtGeneratedTab-${suffix}`;
      const panelId = `PpdtGeneratedPanel-${suffix}`;
      const tab = this.createGeneratedTab(title, tabId, panelId, index);
      const panel = this.createGeneratedPanel(title, tabId, panelId, heading, headings[index + 1], source, index);
      tabsFragment.append(tab);
      panelsFragment.append(panel);
    });

    if (!tabsFragment.childNodes.length) return;
    tabsContainer.replaceChildren(tabsFragment);
    panelsContainer.replaceChildren(panelsFragment);
    this.classList.add('has-generated-description-tabs');
  }

  createGeneratedTab(title, tabId, panelId, index) {
    const tab = document.createElement('button');
    tab.className = `ppdt__tab${index === 0 ? ' is-active' : ''}`;
    tab.id = tabId;
    tab.type = 'button';
    tab.role = 'tab';
    tab.tabIndex = index === 0 ? 0 : -1;
    tab.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
    tab.setAttribute('aria-controls', panelId);
    tab.setAttribute('data-ppdt-tab', '');

    if (this.dataset.showTabNumbers === 'true') {
      const number = document.createElement('span');
      number.className = 'ppdt__tab-number';
      number.textContent = String(index + 1).padStart(2, '0');
      tab.append(number);
    }

    const label = document.createElement('span');
    label.className = 'ppdt__tab-label';
    label.textContent = title;
    const arrow = document.createElement('span');
    arrow.className = 'ppdt__tab-arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';
    tab.append(label, arrow);
    return tab;
  }

  createGeneratedPanel(title, tabId, panelId, heading, nextHeading, source, index) {
    const panel = document.createElement('article');
    panel.className = `ppdt__panel${index === 0 ? ' is-active' : ''}`;
    panel.id = panelId;
    panel.role = 'tabpanel';
    panel.tabIndex = 0;
    panel.hidden = index !== 0;
    panel.setAttribute('aria-labelledby', tabId);
    panel.setAttribute('data-ppdt-panel', '');

    const glow = document.createElement('span');
    glow.className = 'ppdt__panel-glow';
    glow.setAttribute('aria-hidden', 'true');
    panel.append(glow);

    if (this.dataset.showPanelHeading === 'true') {
      const headingRow = document.createElement('div');
      headingRow.className = 'ppdt__panel-heading-row';
      const number = document.createElement('span');
      number.className = 'ppdt__panel-index';
      number.textContent = String(index + 1).padStart(2, '0');
      const panelHeading = document.createElement('h3');
      panelHeading.textContent = title;
      headingRow.append(number, panelHeading);
      panel.append(headingRow);
    }

    const content = document.createElement('div');
    content.className = 'ppdt__content rte';
    const range = document.createRange();
    range.setStartAfter(heading);
    if (nextHeading) range.setEndBefore(nextHeading);
    else if (source.lastChild) range.setEndAfter(source.lastChild);
    const descriptionContent = range.cloneContents();
    content.append(descriptionContent);
    if (!content.textContent?.trim() && !content.querySelector('img, video, ul, ol, table')) {
      const fallback = document.createElement('p');
      fallback.textContent = this.dataset.emptyContent || 'Add content below this H3 heading in the product description.';
      content.append(fallback);
    }
    panel.append(content);

    if (this.dataset.showProductReference === 'true' && this.dataset.productUrl) {
      const link = document.createElement('a');
      link.className = 'ppdt__product-reference';
      link.href = this.dataset.productUrl;
      const linkText = document.createElement('span');
      linkText.textContent = this.dataset.productTitle || 'View product';
      const linkArrow = document.createElement('span');
      linkArrow.setAttribute('aria-hidden', 'true');
      linkArrow.textContent = '↗';
      link.append(linkText, linkArrow);
      panel.append(link);
    }
    return panel;
  }

  setupReveal() {
    if (!('IntersectionObserver' in window)) {
      this.classList.add('is-revealed');
      return;
    }
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.classList.add('is-revealed');
        this.observer?.disconnect();
      },
      { rootMargin: '120px 0px', threshold: 0.05 }
    );
    this.observer.observe(this);
  }
}

if (!customElements.get('product-description-tabs')) {
  customElements.define('product-description-tabs', ProductDescriptionTabs);
}
